import type { MasonryEngineActionHandler } from '@leagueplatform/masonry-engine';
import type { MasonryDriverAction } from '@leagueplatform/masonry-renderers';
import { postDataProcessor } from '@leagueplatform/masonry-api';
import type { UseIntlReturn } from '@leagueplatform/locales';
import { MOBILE_HEALTH_CARD } from 'common/constants';
import { MobileHealthCardLinkoutAction } from 'common/masonry-types';
import {
  setMobileHealthCardLoading,
  setMobileHealthCardError,
  resetMobileHealthCardErrorStore,
  isCurrentMobileHealthCardAttempt,
} from 'masonry/stores/mobile-health-card-error.store';

// One attempt in flight across the whole feature; a duplicate tap is rejected before any event or request.
let isActive = false;

const genericErrorMessage = (intl?: UseIntlReturn): string =>
  intl?.formatMessage({ id: 'STR_MOBILE_HEALTH_CARD_LINKOUT_ERROR' }) ??
  'Something went wrong';

const fieldValue = (
  fields: { id: string; value: unknown }[] | undefined,
  id: string,
): string | undefined => {
  const value = fields?.find((field) => field.id === id)?.value;
  return typeof value === 'string' ? value : undefined;
};

const fillReservedTab = (reservedTab: Window, url: string): void => {
  try {
    // eslint-disable-next-line no-param-reassign
    reservedTab.opener = null;
  } catch {
    // `opener` is read-only in some engines; replace() below still avoids an about:blank history entry.
  }
  reservedTab.location.replace(url);
};

export const mobileHealthCardLinkoutHandler: MasonryEngineActionHandler<
  MobileHealthCardLinkoutAction,
  MasonryDriverAction
> = async (invokedAction, resources) => {
  const intl = resources?.intl as UseIntlReturn | undefined;

  if (isActive) return;

  // Reserve one same-origin tab synchronously, inside the user-activation window, before any async work.
  const reservedTab = window.open('/mhc-loading.html');

  if (!reservedTab) {
    resetMobileHealthCardErrorStore();
    setMobileHealthCardError(genericErrorMessage(intl));
    return;
  }

  isActive = true;
  const attempt = resetMobileHealthCardErrorStore();
  setMobileHealthCardLoading(true);

  const failWith = (message?: string): void => {
    reservedTab.close();
    if (isCurrentMobileHealthCardAttempt(attempt)) {
      setMobileHealthCardError(message ?? genericErrorMessage(intl));
    }
    isActive = false;
  };

  const dataProcessorId =
    invokedAction.payload?.dataProcessorId ??
    MOBILE_HEALTH_CARD.DATA_PROCESSOR_ID;

  try {
    const response = await postDataProcessor({ dataProcessorId });
    const { fields } = response;
    const outcome = fieldValue(fields, MOBILE_HEALTH_CARD.OUTCOME_FIELD_ID);
    const destination = fieldValue(
      fields,
      MOBILE_HEALTH_CARD.DESTINATION_URL_FIELD_ID,
    );
    const message = fieldValue(fields, MOBILE_HEALTH_CARD.MESSAGE_FIELD_ID);

    if (
      response.status === 'success' &&
      outcome === MOBILE_HEALTH_CARD.NAVIGATE_OUTCOME &&
      destination
    ) {
      fillReservedTab(reservedTab, destination);
      if (isCurrentMobileHealthCardAttempt(attempt)) {
        setMobileHealthCardLoading(false);
      }
      isActive = false;
    } else {
      failWith(message);
    }
  } catch {
    failWith();
  }
};
